import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import { PUBLIC_API_ENDPOINT } from '../../constants/platform';
import { getPublicDonatePoll, DonatePoll } from '../../api/payment/donate-poll.api';

/**
 * Overlay BÌNH CHỌN — chèn vào OBS, chạy không cần đăng nhập.
 * Các thanh đua nhau theo thời gian thực mỗi khi có người bình chọn, kèm đếm ngược.
 * Hết giờ thì gắn cúp cho phương án thắng (streamer làm theo phương án đó).
 */
const OUTLINE =
  '0 0 3px rgba(0,0,0,.95), 0 2px 6px rgba(0,0,0,.85), 0 0 14px rgba(0,0,0,.6)';

const formatCa = (v: number) => Number(v || 0).toLocaleString('vi-VN');

const countdown = (s: number) => {
  if (s <= 0) return 'HẾT GIỜ';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return h > 0
    ? `${h}:${`${m}`.padStart(2, '0')}:${`${sec}`.padStart(2, '0')}`
    : `${m}:${`${sec}`.padStart(2, '0')}`;
};

const PageDonatePollLivestream = () => {
  const { contentCreatorId } = useParams<{ contentCreatorId: string }>();
  const [poll, setPoll] = useState<DonatePoll | null>(null);
  const [left, setLeft] = useState(0);

  const load = useCallback(async () => {
    if (!contentCreatorId) return;
    try {
      const p = await getPublicDonatePoll(contentCreatorId);
      setPoll(p);
      setLeft(p?.secondsLeft || 0);
    } catch {
      /* lỗi mạng: giữ nguyên số đang hiện */
    }
  }, [contentCreatorId]);

  useEffect(() => {
    load();
  }, [load]);

  // Đếm ngược tại chỗ cho mượt, không hỏi lại máy chủ mỗi giây.
  useEffect(() => {
    if (left <= 0) return undefined;
    const t = setInterval(() => setLeft((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [left > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!contentCreatorId) return undefined;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${PUBLIC_API_ENDPOINT}/payment-hubs?userid=${contentCreatorId}`, {
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection
      .start()
      .then(() => {
        // Có người bình chọn -> lấy lại số đúng từ máy chủ thay vì tự cộng ở client.
        connection.on('ReceivePollUpdate', () => load());
      })
      .catch(() => {
        /* không nối được hub -> vẫn hiện số ở lần tải đầu */
      });

    return () => {
      connection.stop();
    };
  }, [contentCreatorId, load]);

  // Chưa mở bình chọn -> không vẽ gì để OBS trong suốt hoàn toàn.
  if (!poll?.exists || !poll.isActive) return null;

  const ended = poll.isEnded || left <= 0;

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-3 bg-transparent p-4">
      <div className="w-full max-w-[640px]">
        <div className="flex items-baseline justify-between gap-3">
          <div
            className="min-w-0 break-words text-3xl font-extrabold text-white"
            style={{ textShadow: OUTLINE }}
          >
            {poll.question}
          </div>
          <div
            className={`shrink-0 text-2xl font-extrabold ${ended ? 'text-red-300' : 'text-yellow-300'}`}
            style={{ textShadow: OUTLINE }}
          >
            {countdown(left)}
          </div>
        </div>

        <div className="mt-3 flex flex-col gap-3">
          {poll.options.map((o) => (
            <div key={o.id}>
              <div
                className="flex items-baseline justify-between gap-2 text-xl font-extrabold text-white"
                style={{ textShadow: OUTLINE }}
              >
                <span className="min-w-0 break-words">
                  {o.label}
                  {ended && o.isLeading && ' 🏆'}
                </span>
                <span className="shrink-0">
                  {formatCa(o.totalAmount)} Ca · {o.percent}%
                </span>
              </div>
              <div className="mt-1 h-7 w-full overflow-hidden rounded-full bg-black/40 ring-2 ring-black/30">
                <div
                  className={`h-full rounded-full transition-[width] duration-700 ease-out ${
                    o.isLeading ? 'bg-yellow-400' : 'bg-[#39539E]'
                  }`}
                  style={{ width: `${o.percent}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PageDonatePollLivestream;
